/**
 * Copy for the Explore surface.
 *
 * The backend returns i18n *keys* rather than display strings —
 * `placeholder_key`, `subtitle_key`, `dialogue_type_label_key`, `cta_key`
 * (aris-ai `core/explore/contracts.py`) — and neither service ships a locale
 * bundle. This app has no i18n framework either, so the mapping lives here.
 * Strings are transcribed from Figma node 9285:17268.
 *
 * See docs/explore-implementation-plan.md §3 — this is a known gap, not a
 * design: when a real locale bundle exists this file should read from it.
 */

const COPY = {
  "cta.take_the_next_step": "Take the Next Step",
  // Card CTAs (v26.5 item 48). The server picks Start or Continue from the
  // member's own progress; the client renders the key it was given.
  "cta.start_dialogue": "Start Dialogue",
  "cta.continue_dialogue": "Continue Dialogue",
  "dialogue.between_conversations":
    "Between Dialogues, Aris may recommend reflections, practices, or check-ins based on your progress.",
  // Life Area feed (aris-ai life_area_service.py) — strings from Figma 9139:19781.
  "explore.life_areas.subtitle": "Explore guidance for every area of your life.",
  "explore.life_area.recommended_for_you.title": "Recommended for You",
  "explore.life_area.explore_more.title": "Explore More",
  "explore.life_area.talk_with_aris.title": "Talk with Aris",
  "explore.life_area.talk_with_aris.prompt":
    "Not sure where to begin? Talk with Aris about what's happening, and Aris will help you identify the next best conversation.",
  // Self-Knowledge area (aris-ai self_knowledge_area.py).
  "explore.self_knowledge.hero.description":
    "Understand yourself through your Elements.",
  "explore.self_knowledge.continue_discoveries.title": "Continue Your Discoveries",
  "explore.self_knowledge.complete.title": "Self-Knowledge Complete",
  "explore.self_knowledge.complete.body":
    "You've completed all five Discoveries. Your Periodic Table of the Self™ is now the primary way to explore who you are.",
  "explore.self_knowledge.personal_model.title": "Explore Your Elements",
  "explore.self_knowledge.personal_model.description":
    "Discover your strengths, natural tendencies, and what makes you uniquely you.",
  "explore.self_knowledge.personal_model.cta": "View Your Elements",
  "explore.self_knowledge.recommended_for_you.title": "Recommended for You",
  "explore.self_knowledge.talk_with_aris.title": "Talk with Aris",
  "explore.self_knowledge.talk_with_aris.prompt":
    "Not sure where to begin? Talk with Aris about what's happening, and Aris will help you identify the next best conversation.",
};

/** Resolve a backend copy key, falling back to `fallback` then the key itself. */
export const t = (key, fallback) => {
  if (!key) return fallback || "";
  return COPY[key] || fallback || key;
};

/** Card badges. At most one per card, resolved server-side. */
export const BADGE_LABEL = {
  SAVED: "Saved",
  PREVIOUSLY_EXPERIENCED: "Revisit",
};

/** The Explore page header (Figma 9154:8266). */
export const EXPLORE_COPY = {
  // A card whose artwork is still being made (gaps E4): no Start yet.
  generating: "Aris is preparing this dialogue…",
  title: "Explore",
  subtitle:
    "Personalized Dialogues for your life, created around who you are and what matters to you.",
};

/** Section headings. The backend sends only `subtitle_key`, never a title. */
export const SECTION_COPY = {
  TALK_WITH_ARIS: {
    title: "Can't find what you're looking for?",
    subtitle:
      "Talk with Aris about what's on your mind. If there's a dialogue that fits, Aris will recommend it. Otherwise, Aris will guide you personally.",
    cta: "Talk with Aris",
    voice: "Voice",
    text: "Text",
  },
  ACTIVE_DIALOGUES: {
    title: "Active Dialogues",
    subtitle: "Pick up conversations that are still unfolding.",
    viewAll: "View All",
  },
  PERSONAL_DIALOGUES: {
    title: "Personal Dialogues",
    subtitle:
      "Dialogues created from conversations, documents, recordings, and other things you've shared.",
    library: "Your Personal Dialogue Library",
    seeAll: "See All",
    capture: "Capture Something New",
  },
  // No Figma node or product copy yet (WEB_INTEGRATION §6 item 2, O5). Placed
  // just above Talk with Aris, as the integration doc directs.
  SUGGESTED_ACTIONS: {
    title: "Suggested for You",
    subtitle: "Small steps Aris thinks could help right now.",
  },
  // Defined by the contract but not yet served (ATIT-65). No Figma node.
  HBR_DIALOGUES: {
    title: "Harvard Business Review",
    subtitle: "Dialogues built from Harvard Business Review ideas and frameworks.",
  },
  RECOMMENDED_FOR_YOU: {
    title: "Recommended for You",
  },
  LIFE_AREAS: {
    title: "Life Areas",
    subtitle: "Explore guidance for every area of your life.",
  },
  EXPERT_DIALOGUES: {
    title: "Expert Dialogues",
    subtitle:
      "Personalized Dialogues built from the ideas and frameworks of leading experts.",
  },
};

export const ORGANIZATION_COPY = {
  titlePrefix: "From",
  titleFallback: "From your organization",
  subtitle:
    "Dialogues created from the knowledge and resources your organization has shared with Aris.",
};

/** Active Dialogues rail (Figma 9154:8266). */
export const ACTIVE_COPY = {
  cta: "Continue",
  lastConversation: "Last conversation:",
  // `awaiting_followup` has no Figma copy yet (§7 design gap).
  followup: "Aris is waiting to hear how it went",
};

/** The Voice / Text chooser (Figma 10792:21207). */
export const MODALITY_COPY = {
  startTitle: "Start this Dialogue",
  continueTitle: "Continue this Dialogue",
  subtitle: "Take a minute to get clear, calm, and confident before your conversation.",
  voice: "Voice",
  voiceHint: "Speak with Aris",
  text: "Text",
  textHint: "Type with Aris",
};

/**
 * Personal Dialogue source kinds. `source_kind` is being added to aris-ai (§7
 * E5) and its values follow the DCI creation methods, so the known spellings
 * are matched loosely; anything else falls back to a neutral row with no label.
 */
export const SOURCE_KIND_LABEL = {
  RECORDING: "Recording",
  DOCUMENT: "Document",
  PHOTO: "Photo",
  TEXT: "Conversation",
};

/** The Personal Dialogue Library page (Figma 10873:24975). */
export const LIBRARY_COPY = {
  title: "Your Personal Dialogue Library",
  listLabel: "Your Personal Dialogue Library",
  empty: "You haven't created any Personal Dialogues yet.",
  unavailable: "Personal Dialogues aren't available on your account yet.",
  error: "We couldn't load your Personal Dialogues. Please try again.",
  archived: "Archived",
  searchLabel: "Search your Personal Dialogues",
  noMatch: "None of your Personal Dialogues match that search.",
};

/**
 * Dialogue Detail (Figma 10792:20946; guide §7.1). The payload sends content,
 * not headings, so the labels live here.
 */
export const DETAIL_COPY = {
  notFound: "We couldn't find this dialogue. It may no longer be available.",
  typicalLength: "Dialogue length",
  // Keyed by `why_kind`; no panel for any other value.
  why: {
    WHY_ARIS_RECOMMENDED_THIS: "Why Aris Recommended This",
    WHY_THIS_DIALOGUE_WAS_CREATED: "Why this Dialogue was created",
  },
  personalizeTitle: "How Aris Will Personalize This",
  personalizeLead: "This dialogue adapts as you grow.",
  atAGlance: "At a glance",
  researchTitle: "Research behind this dialogue",
  researchLink: "View the research",
  startCta: "Start this Dialogue",
  continueCta: "Continue Dialogue",
  private: "Your conversation is private and secure",
  // The member's own state, shown above the button. NOT_STARTED and ACTIVE
  // need no note; PAUSED and ARCHIVED get the lifecycle banner instead.
  state: {
    COMPLETED: "You've completed this Dialogue. You can revisit it any time.",
  },
};

/**
 * Pause / resume / archive (Figma 11115:27004, "Active Dialogues"). Archive
 * is final (PM 2026-09-29): there is no Restore anywhere, so the archive copy
 * never promises one — the Figma's "you can restore it anytime" lines are
 * replaced here and flagged for design.
 */
export const LIFECYCLE_COPY = {
  // Hero badge by member-facing state. COMPLETED has no badge in Figma.
  badge: {
    ACTIVE: "IN PROGRESS",
    PAUSED: "PAUSED",
    ARCHIVED: "ARCHIVED",
  },
  // aria-labels for the hero's icon buttons.
  aria: {
    pause: "Pause dialogue",
    resume: "Resume dialogue",
    archive: "Archive dialogue",
  },
  resumeCta: "Resume Dialogue",
  banner: {
    PAUSED: {
      title: "This dialogue is paused.",
      body: "You can resume it anytime.",
    },
    ARCHIVED: {
      title: "This dialogue is archived.",
      body: "Your conversation history is saved.",
    },
  },
  sheet: {
    close: "Close",
    cancel: "Cancel",
    pause: {
      title: "Pause this Dialogue?",
      body: [
        "This will remove it from your active dialogues and pause any reminders or communications related to this dialogue.",
        "You can resume it anytime from your paused dialogues.",
      ],
      confirm: "Pause Dialogue",
    },
    archive: {
      title: "Archive this Dialogue?",
      body: [
        "This will remove it from your active dialogues and stop any reminders or communications related to this dialogue.",
        "Your conversation history will be saved. Archiving is permanent.",
      ],
      confirm: "Archive Dialogue",
    },
  },
  // Toasts have no Figma copy yet.
  toast: {
    PAUSED: "Dialogue paused",
    ACTIVE: "Dialogue resumed",
    ARCHIVED: "Dialogue archived",
    conflict: "This dialogue has already changed. Showing its latest state.",
    error: "We couldn't update this dialogue. Please try again.",
    unavailable: "Pausing and archiving aren't available on your account yet.",
  },
};

/**
 * The Active Dialogues list (Figma 11115:28057 / 28117 / 28167). Completed and
 * the empty states have no Figma frame; they follow the same pattern.
 */
export const ACTIVE_LIST_COPY = {
  title: "Active Dialogues",
  tabsLabel: "Dialogues by state",
  tabs: {
    ACTIVE: "Active",
    PAUSED: "Paused",
    COMPLETED: "Completed",
    ARCHIVED: "Archived",
  },
  tabWithCount: (label, n) => `${label} (${n})`,
  // Card meta per tab: "Last conversation · 3 days ago", "Paused · Aug 26, 2026".
  meta: {
    ACTIVE: "Last conversation",
    PAUSED: "Paused",
    COMPLETED: "Completed",
    ARCHIVED: "Archived",
  },
  empty: {
    ACTIVE: "You don't have any active dialogues right now.",
    PAUSED: "No paused dialogues yet.",
    COMPLETED: "No completed dialogues yet.",
    ARCHIVED: "No archived dialogues yet.",
  },
  loading: "Loading your dialogues…",
  error: "We couldn't load your dialogues. Please try again.",
  retry: "Try again",
};

/** Research Behind This Dialogue + Research Source (Figma 10873:22838 / 22543). */
export const RESEARCH_COPY = {
  title: "Research Behind This Dialogue",
  subtitle: "Why you can trust the knowledge behind this conversation.",
  unavailable: "The research for this Dialogue isn't available right now.",
  informed: "How this Dialogue is informed",
  findings: "What the research tells us",
  limitations: "Limitations:",
  howAris: "How Aris uses this research",
  sources: (n) => `Research & Sources (${n})`,
  about: "About our research",
  sourceTitle: "Research Source",
  sourceSubtitle: "A closer look at the evidence behind this Dialogue.",
  sourceError: "Some details for this source couldn't be loaded.",
  aboutSource: "About this Source",
  institution: "Institution",
  authors: "Authors / Researchers",
  evidenceType: "Type of evidence",
  whyUses: "Why Aris uses this source",
  supports: "What this source supports in this Dialogue",
  limitationsTitle: "Important limitations",
  viewOriginal: "View Original Source",
  copyLink: "Copy Link",
  copied: "Link copied",
};

/** Life Area feed chrome (Figma node 9139:19781). */
export const LIFE_AREA_COPY = {
  empty: "Aris is still preparing dialogues for this area.",
  talkCta: "Talk with Aris",
};

/**
 * Discovery display titles, keyed by aris-core `DISCOVERY_SEQUENCE`. The
 * Self-Knowledge payload carries only `discovery_key` — no member-facing
 * title — so the mapping lives here like every other copy key.
 */
export const DISCOVERY_TITLE = {
  D1_HOW_YOU_SHOW_UP: "How You Show Up",
  D2_HOW_YOU_THINK: "How You Think",
  D3_HOW_YOU_HANDLE_CONFLICT: "How You Handle Conflict",
  D4_WHAT_DRIVES_YOU: "What Drives You",
  D5_HOW_YOU_MAKE_DECISIONS: "How You Make Decisions",
};

/** Discovery status → chip label (aris-core `DiscoveryStatus`). */
export const DISCOVERY_STATUS_LABEL = {
  COMPLETED: "Completed",
  AVAILABLE: "Next Up",
  ACTIVE: "In Progress",
  LOCKED: "Locked",
  NOT_STARTED: "Locked",
  EMERGES: "Emerges",
};

/** The preparing view (Figma 9154:8266, left frame). */
export const PREPARING_COPY = {
  eyebrow: "ARIS IS PREPARING",
  title: "Preparing your Explore experience",
  body: [
    "I'm building an Explore experience around your Mission, your Identity, and what matters most to you.",
    "Every recommendation is selected specifically for you—so what you discover reflects your life, not generic content.",
  ],
  whyTitle: "Why does this take time?",
  whyBody:
    "Aris isn't simply generating recommendations. It's organizing a personalized dialogue library around who you are, what matters most to you, and where you're trying to go—then personalizing each dialogue again when you begin the conversation.",
  waitTitle: "You don't have to wait.",
  waitBody:
    "While your Explore experience is being prepared, you can talk with Aris any time. Every conversation deepens understanding and makes future guidance even more personal.",
  waitCta: "Talk with Aris",
  // aris-ai sends no notification on READY (§7 P4), so there is deliberately
  // no "Aris will notify you" line. The hours come from `estimated_ready_by`.
  noticeLead: "Your personalized Explore experience will be ready ",
  noticeWithin: (hours) => `within ${hours} ${hours === 1 ? "hour" : "hours"}.`,
  footnote:
    "Your Journey has already begun. Every conversation you have with Aris helps your Explore experience become even more personal over time.",
};

/**
 * Checklist copy, keyed exactly by the payload's `title_key` / `subtitle_key`
 * (no server-side string table exists yet, §7 P3). `focus_established` is gone
 * from the payload and `preparing_journeys` is untracked and never drawn.
 */
export const PREPARING_STEP_COPY = {
  "prep.mission_understood.title": "Mission understood",
  "prep.mission_understood.subtitle": "Your purpose is the foundation.",
  "prep.priorities_connected.title": "Current Priorities connected",
  "prep.priorities_connected.subtitle": "Aligned with what matters most today.",
  "prep.preparing_dialogues.title": "Preparing Personalized Dialogues",
  "prep.preparing_dialogues.subtitle": "Selecting and shaping dialogues that matter to you.",
  "prep.building_experience.title": "Building your Explore experience",
  "prep.building_experience.subtitle": "Almost there—curating everything for you.",
};

/**
 * The tracked steps in order, for when the preparation read fails: drawn with
 * no state claimed, so a failed read never looks like finished work.
 */
export const PREPARING_FALLBACK_STEPS = [
  "mission_understood",
  "priorities_connected",
  "preparing_dialogues",
  "building_experience",
].map((key, i) => ({
  key,
  order: i + 1,
  state: "PENDING",
  tracked: true,
  title_key: `prep.${key}.title`,
  subtitle_key: `prep.${key}.subtitle`,
}));

/**
 * The Personal Dialogue part of the Overview (DCI v27 Part I §3; aris-ai plan 53).
 */
export const PERSONAL_COPY = {
  sectionLabel: "About this Personal Dialogue",
  captureMethod: "Capture Method",
  source: "Source",
  providedBy: "Provided By",
  relationship: "Relationship",
  unconfirmed: "Source details were identified by Aris and haven't been confirmed.",
  summary: "Summary",
  takeaways: "Key Takeaways",
  proposals: "Proposed Actions & Commitments",
  proposalsNote: "You'll review and confirm these during your first Dialogue with Aris.",
  edit: "Edit",
  title: "Title",
  onePerLine: "one per line",
  staleNote:
    "Changing the source, summary, takeaways or proposals marks earlier research as out of date.",
  cancel: "Cancel",
  save: "Save",
  saving: "Saving…",
  saved: "Your Personal Dialogue was updated.",
  saveError: "We couldn't save your changes. Please try again.",
  conflict: "This Personal Dialogue changed elsewhere — we've reloaded it.",
};
